import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { humanizeAuthError } from '../lib/authErrors';
import { setRememberMe, getRememberMe } from '../lib/supabaseClient';
import { Pencil, Mail, Lock, ArrowLeft, Globe, MailCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { isCapacitorIOS, isIOSStandaloneApp } from '../lib/platform';

export default function AuthPage() {
  const { signIn, signUp, resetPassword, signInWithGoogle, signInWithApple, resendConfirmation } = useAuth();
  const { t, lang, setLang } = useT();
  const [mode, setMode] = useState('login'); // login | signup | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(getRememberMe());
  const [loading, setLoading] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [signupSuccessEmail, setSignupSuccessEmail] = useState('');

  // Apple Guideline 4.8 — cacher le login Google tiers sur iOS App Store et PWA.
  // OAuth Google ouvre Safari (rejet Guideline 4). Email/password + Sign in with
  // Apple couvrent les options privacy-preserving requises.
  const hideThirdPartyLogin = isCapacitorIOS() || isIOSStandaloneApp();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsConfirmation(false);
    // Apply the persistence choice BEFORE the auth call so Supabase writes the
    // session to the right storage from the very first time.
    setRememberMe(remember);
    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          toast.error(humanizeAuthError(error, lang));
          if ((error.message || '').toLowerCase().includes('not confirmed')) {
            setNeedsConfirmation(true);
          }
        }
      } else if (mode === 'signup') {
        const { data, error } = await signUp(email, password);
        if (error) toast.error(humanizeAuthError(error, lang));
        else if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
          // Supabase silently returns success with an empty identities array
          // when the email is already registered (anti-enumeration protection).
          // Detect that case and tell the user clearly instead of showing the
          // "check your inbox" modal for an email that will never arrive.
          toast.error(t('auth.email_already_used'));
          setMode('login');
        } else {
          // Show a clear modal instead of just a toast so the user
          // CAN'T miss it on mobile and knows to check the spam folder.
          setSignupSuccessEmail(email);
          setNeedsConfirmation(true);
          setMode('login');
        }
      } else {
        const { error } = await resetPassword(email);
        if (error) toast.error(humanizeAuthError(error, lang));
        else {
          toast.success(t('auth.reset_sent'));
          setMode('login');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (!email) {
      toast.error(lang === 'fr' ? 'Saisissez votre courriel' : 'Enter your email');
      return;
    }
    setLoading(true);
    try {
      const { error } = await resendConfirmation(email);
      if (error) toast.error(humanizeAuthError(error, lang));
      else toast.success(lang === 'fr' ? '✓ Nouveau courriel envoyé' : '✓ New email sent');
    } finally {
      setLoading(false);
    }
  };

  const google = async () => {
    setRememberMe(remember);
    const { error } = await signInWithGoogle();
    if (error) toast.error(humanizeAuthError(error, lang));
  };

  const apple = async () => {
    setRememberMe(remember);
    const { error } = await signInWithApple();
    if (error) toast.error(humanizeAuthError(error, lang));
  };

  // Compute the small eyebrow label and the submit-button label per mode.
  // Extracted to avoid nested ternaries inside JSX.
  const modeLabel = (() => {
    if (mode === 'signup') return t('auth.signup');
    if (mode === 'forgot') return t('auth.forgot');
    return t('auth.login');
  })();
  const submitLabel = (() => {
    if (loading) return t('common.loading');
    if (mode === 'signup') return t('auth.signup_btn');
    if (mode === 'forgot') return t('auth.reset_btn');
    return t('auth.signin_btn');
  })();

  return (
    <div className="min-h-screen bg-white grain flex flex-col">
      <div className="flex justify-between items-center px-4 sm:px-6 h-16">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center">
            <Pencil size={18} strokeWidth={2.4} />
          </div>
          <span className="font-heading font-black">SoumissionPro</span>
        </div>
        <button
          data-testid="lang-toggle"
          onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border-2 border-gray-200 text-sm font-bold"
        >
          <Globe size={14} /> {lang.toUpperCase()}
        </button>
      </div>

      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-3">
              {modeLabel}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05]">
              {t('app.name')}.
            </h1>
            <p className="text-gray-500 mt-2 text-base">{t('app.tagline')}</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">{t('auth.email')}</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                <input
                  data-testid="auth-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field !pl-12"
                  placeholder="vous@entreprise.ca"
                />
              </div>
            </div>
            {mode !== 'forgot' && (
              <div>
                <label className="label">{t('auth.password')}</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                  <input
                    data-testid="auth-password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="field !pl-12"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            )}

            {mode !== 'forgot' && (
              <label
                data-testid="remember-me-label"
                className="flex items-center gap-2.5 cursor-pointer select-none py-1"
              >
                <input
                  data-testid="remember-me"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-[18px] h-[18px] rounded border-2 border-[#E5DCC9] accent-[#7A1F2B] cursor-pointer"
                />
                <span className="text-sm text-gray-700">
                  {lang === 'fr' ? 'Rester connecté sur cet appareil' : 'Stay signed in on this device'}
                </span>
              </label>
            )}

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {submitLabel}
            </button>

            {mode !== 'forgot' && (
              <>
                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t('auth.or')}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                {/* Sign in with Apple — Apple recommande de placer leur bouton
                    EN PREMIER si plusieurs options de connexion tierces sont
                    proposées (HIG: Sign in with Apple). Affiché PARTOUT, y
                    compris en PWA standalone iOS (c'est le but : satisfaire
                    Apple Guideline 4.8). */}
                <button
                  data-testid="auth-apple"
                  type="button"
                  onClick={apple}
                  className="w-full inline-flex items-center justify-center gap-2.5 py-3 rounded-xl font-semibold transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#000000', color: '#FFFFFF' }}
                >
                  <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                  </svg>
                  {t('auth.apple')}
                </button>
                {!hideThirdPartyLogin && (
                  <button
                    data-testid="auth-google"
                    type="button"
                    onClick={google}
                    className="btn-outline w-full gap-3 mt-3"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    {t('auth.google')}
                  </button>
                )}
              </>
            )}
          </form>

          <div className="mt-8 text-center space-y-3">
            {needsConfirmation && (
              <div data-testid="resend-confirmation-banner" className="card border-2 border-amber-300 bg-amber-50 p-4 text-left mb-4">
                <div className="text-sm font-bold text-amber-900 mb-2">
                  {lang === 'fr' ? 'Courriel non confirmé' : 'Email not confirmed'}
                </div>
                <div className="text-xs text-amber-800 mb-3 leading-relaxed">
                  {lang === 'fr'
                    ? "Vérifiez votre boîte (et vos spams). Le lien doit être ouvert sur ce même appareil. Sinon, demandez un nouveau lien :"
                    : 'Check your inbox (and spam). Open the link on this same device. Otherwise, request a new one:'}
                </div>
                <button
                  data-testid="resend-confirmation-btn"
                  onClick={resend}
                  disabled={loading}
                  className="btn-outline w-full !min-h-[44px] !text-sm"
                >
                  {lang === 'fr' ? 'Renvoyer le courriel de confirmation' : 'Resend confirmation email'}
                </button>
              </div>
            )}
            {mode === 'login' && (
              <>
                <button data-testid="link-forgot" onClick={() => setMode('forgot')} className="text-sm font-bold text-[#7A1F2B] hover:underline">
                  {t('auth.forgot')}
                </button>
                <div className="text-sm text-gray-500">
                  {t('auth.no_account')}{' '}
                  <button data-testid="link-signup" onClick={() => setMode('signup')} className="font-bold text-[#7A1F2B] hover:underline">
                    {t('auth.signup')}
                  </button>
                </div>
              </>
            )}
            {mode === 'signup' && (
              <div className="text-sm text-gray-500">
                {t('auth.have_account')}{' '}
                <button data-testid="link-login" onClick={() => setMode('login')} className="font-bold text-[#7A1F2B] hover:underline">
                  {t('auth.login')}
                </button>
              </div>
            )}
            {mode === 'forgot' && (
              <button data-testid="link-back" onClick={() => setMode('login')} className="inline-flex items-center gap-2 text-sm font-bold text-[#7A1F2B] hover:underline">
                <ArrowLeft size={14} /> {t('auth.back')}
              </button>
            )}
          </div>

        </div>
      </div>
      <footer className="px-4 sm:px-6 py-4 text-center">
        <div className="text-[11px] text-gray-400 space-x-3">
          <a href="/support" className="hover:underline" data-testid="auth-footer-support-link">{lang === 'fr' ? 'Aide & Support' : 'Help & Support'}</a>
          <span>·</span>
          <a href="/terms" className="hover:underline">{lang === 'fr' ? 'Conditions' : 'Terms'}</a>
          <span>·</span>
          <a href="/privacy" className="hover:underline">{lang === 'fr' ? 'Confidentialité' : 'Privacy'}</a>
        </div>
        <div className="mt-3 inline-flex items-center gap-1 rounded-full border border-[#E5DCC9] bg-white p-0.5" data-testid="auth-footer-lang-toggle">
          <button
            type="button"
            onClick={() => setLang('fr')}
            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest transition-colors ${lang === 'fr' ? 'bg-[#7A1F2B] text-white' : 'text-gray-500 hover:text-[#7A1F2B]'}`}
            data-testid="auth-footer-lang-fr"
            aria-pressed={lang === 'fr'}
          >
            FR
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest transition-colors ${lang === 'en' ? 'bg-[#7A1F2B] text-white' : 'text-gray-500 hover:text-[#7A1F2B]'}`}
            data-testid="auth-footer-lang-en"
            aria-pressed={lang === 'en'}
          >
            EN
          </button>
        </div>
      </footer>

      {/* === Signup success modal: forces user to read about spam folder === */}
      {signupSuccessEmail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/60 backdrop-blur-sm"
          data-testid="signup-success-modal"
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-7 relative shadow-2xl">
            <button
              type="button"
              onClick={() => setSignupSuccessEmail('')}
              className="absolute top-4 right-4 text-gray-400 p-1"
              data-testid="signup-modal-close"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>
            <div
              className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
              style={{ backgroundColor: '#7A1F2B', color: '#C19A57' }}
            >
              <MailCheck size={32} />
            </div>
            <h2 className="font-heading text-2xl font-black text-center mb-2" style={{ color: '#5A1620' }}>
              {lang === 'fr' ? 'Compte créé !' : 'Account created!'}
            </h2>
            <p className="text-center text-sm text-gray-600 mb-5">
              {lang === 'fr' ? 'Un courriel de confirmation a été envoyé à' : 'A confirmation email was sent to'}
            </p>
            <div className="bg-[#F5EFE0] border border-[#E5DCC9] rounded-xl px-4 py-2.5 text-center mb-5">
              <span className="font-bold text-sm break-all" style={{ color: '#5A1620' }}>{signupSuccessEmail}</span>
            </div>

            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-5">
              <div className="flex items-start gap-2.5">
                <div className="text-2xl">⚠️</div>
                <div>
                  <div className="font-black text-amber-900 text-sm mb-1">
                    {lang === 'fr' ? 'Vérifie ta boîte de spam !' : 'Check your spam folder!'}
                  </div>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    {lang === 'fr'
                      ? "Le courriel arrive parfois dans les indésirables (spam/courrier indésirable). Si tu ne le vois pas dans ta boîte de réception après 2 minutes, vérifie ce dossier."
                      : 'Sometimes the email lands in junk/spam. If you don\'t see it in your inbox after 2 minutes, check that folder.'}
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 text-center mb-5">
              {lang === 'fr'
                ? 'Une fois confirmé, reviens ici pour te connecter.'
                : 'Once confirmed, come back here to sign in.'}
            </p>

            <button
              type="button"
              onClick={() => setSignupSuccessEmail('')}
              className="btn-primary w-full"
              data-testid="signup-modal-ok"
            >
              {lang === 'fr' ? "J'ai compris" : 'Got it'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
